<template>
  <div class="flex justify-center items-center">
    <div class="pb-10 w-full">
      <h3>{{ $t('profitSharingPlanBook.form.add') }}</h3>
      <Form
        v-slot="{ errors, meta, resetForm }"
        :validation-schema="schema"
        @submit="handleForm"
        @reset="handleReset"
      >
        <div class="w-2/5 mt-3">
          <label
            class="text-xs uppercase font-semibold text-secondary9"
            for="name"
            >{{ $t('profitSharingPlanBook.fields.name') }}</label
          >
          <Field v-slot="{ field }" v-model="newPlan.name" name="name">
            <Input
              v-bind="field"
              id="name"
              v-model="newPlan.name"
              name="name"
              :disabled="false"
              :placeholder="$t('profitSharingPlanBook.placeholder.name')"
            />
          </Field>
        </div>
        <div class="w-2/5 mt-3">
          <label
            class="text-xs uppercase font-semibold text-secondary9"
            for="colorCode"
            >{{ $t('profitSharingPlanBook.fields.color') }}</label
          >
          <Field
            v-slot="{ field }"
            v-model="newPlan.colorCode"
            name="colorCode"
          >
            <div class="group-color-block">
              <color-picker
                name="colorCode"
                :value="newPlan.colorCode"
                v-bind="field"
                @change="newPlan.colorCode = $event"
              />
            </div>
          </Field>
        </div>

        <div class="w-2/5 mt-3">
          <label
            class="text-xs uppercase font-semibold text-secondary9"
            for="description"
            >{{ $t('profitSharingPlanBook.fields.description') }}</label
          >
          <Field
            v-slot="{ field }"
            v-model="newPlan.description"
            name="description"
          >
            <div class="group-color-block">
              <textarea
                v-bind="field"
                id=""
                :placeholder="
                  $t('profitSharingPlanBook.placeholder.description')
                "
                name="description"
                class="text-area"
              ></textarea>
            </div>
          </Field>
        </div>

        <div v-if="plan?.id && canDo" class="mt-5">
          <h3 class="text-md font-bold">{{ $t('global.settings') }}</h3>
          <div
            class="
              book__settings
              flex
              justify-between
              items-center
              border border-error
              rounded
              p-3
              mt-3
              text-xl
            "
          >
            <div>
              <h5 class="text-blackp">
                {{ $t('profitSharingPlanBook.form.deletionTitle') }}
              </h5>
              <h6>{{ $t('profitSharingPlanBook.form.deletionMessage') }}</h6>
            </div>
            <div class="ml-auto">
              <el-popconfirm
                :title="$t('profitSharingPlanBook.form.promptDeletion')"
                :confirm-button-text="$t('confirm.yes')"
                :cancel-button-text="$t('confirm.no')"
                @confirm="deleteGroup"
              >
                <template #reference>
                  <Button
                    variant="custom"
                    class="bg-error text-white hover:bg-error hover:border-0"
                    :disabled="false"
                    :label="$t('profitSharingPlanBook.form.deletionPersist')"
                  >
                  </Button>
                </template>
              </el-popconfirm>
            </div>
            <i
              v-if="false"
              v-tooltip.bottom="
                `Ce titre est utilisé dans un ou plusieurs évènements.`
              "
              class="bi-lock-fill text-warning xxl"
            />
          </div>
        </div>
        <FormActions
          v-if="canDo"
          :errors="errors"
          :meta="meta"
          @reset-form="resetForm"
        />
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
  import { computed, defineComponent, onMounted, ref, watch } from 'vue'
  import { useStore } from 'vuex'
  import { Form, Field } from 'vee-validate'
  import { ProfitSharingPlan } from '@/services/api/models'
  import { ProfitSharingPlanFormData } from '@/types'
  import { Input, Button } from '@up.law/uplaw-ui'
  import FormActions from '@/components/Forms/Fields/ModalActions.vue'
  import { CompanyModel } from '@/services/api/models1'
  import {
    EDIT_PROFIT_SHARING_PLAN,
    CREATE_PROFIT_SHARING_PLAN
  } from '@/store/modules/Organization/models'
  import { TYPE } from 'vue-toastification'
  import { useI18n } from 'vue-i18n'
  import { groupSchema } from '@/utils/schema/group'
  import ColorPicker from '@/components/ColorPicker/index.vue'
  import { ElPopconfirm } from 'element-plus'
  import { useAcl } from 'vue-simple-acl/src'

  export default defineComponent({
    name: 'UplContactGroup',
    components: {
      Form,
      ElPopconfirm,
      Field,
      Input,
      FormActions,
      Button,
      ColorPicker
    },
    emits: [],
    setup() {
      const store = useStore()
      const { t } = useI18n()
      const colorCode = ref('#409EFF')
      const newPlan = ref<ProfitSharingPlanFormData>({
        name: '',
        colorCode: '',
        description: null
      })

      const resetForm = () => {
        newPlan.value = {
          name: '',
          colorCode: '',
          description: null
        }
      }

      const acl = useAcl()
      const canDo = computed(
        () =>
          acl.role('admin') ||
          (acl.permission('is-manager') &&
            acl.permission('write-profitSharingPlan'))
      )

      const modalForm = computed(() => store.getters['organizations/form'])

      const plan = computed<ProfitSharingPlan | null>(
        () => store.getters['organizations/profitSharingPlan']
      )

      const currentCompany = computed<CompanyModel>(
        () => store.getters['organizations/contextCompany']
      )

      const schema = ref(groupSchema)

      const initForm = () => {
        store.commit('organizations/SET_PROFIT_SHARING_PLAN', null)
        store.dispatch('organizations/INIT_MODAL_FORM', {
          ...store.getters['organizations/form'],
          modalInit: true,
          initCurrentForm: true
        })
      }

      const handleForm = (values: ProfitSharingPlanFormData) => {
        let edition = false
        let action = CREATE_PROFIT_SHARING_PLAN
        let formData: any = {
          companyId: currentCompany.value.id,
          data: { ...values },
          planId: ''
        }
        edition = !!plan.value?.id
        formData['planId'] = plan.value?.id
        action = plan.value?.id
          ? EDIT_PROFIT_SHARING_PLAN
          : CREATE_PROFIT_SHARING_PLAN

        store.dispatch(`organizations/${action}`, {
          ...formData,
          onError: (error = null) => {
            const message =
              error ||
              t(
                `profitSharingPlanBook.apiResponse.${
                  edition ? 'edit' : 'create'
                }Error`
              )
            store.dispatch('toolbox/displayToast', {
              type: TYPE.ERROR,
              context: t('profitSharingPlanBook.form.title'),
              message
            })
          },
          onSuccess: () => {
            initForm()
            resetForm()
            store.dispatch('toolbox/displayToast', {
              type: TYPE.SUCCESS,
              context: t('profitSharingPlanBook.form.title'),
              message: t(
                `profitSharingPlanBook.apiResponse.${
                  edition ? 'edit' : 'create'
                }Success`
              )
            })
          }
        })
      }

      const deleteGroup = () => {
        store.dispatch(`organizations/REMOVE_PROFIT_SHARING_PLAN`, {
          companyId: currentCompany.value.id,
          id: plan.value?.id,
          onSuccess: () => {
            initForm()
            resetForm()
            store.dispatch('toolbox/displayToast', {
              type: TYPE.SUCCESS,
              context: t('profitSharingPlanBook.form.title'),
              message: t(`profitSharingPlanBook.apiResponse.deleteSuccess`)
            })
          },
          onError: (error = null) => {
            const message =
              error || t(`profitSharingPlanBook.apiResponse.deleteError`)
            store.dispatch('toolbox/displayToast', {
              type: TYPE.ERROR,
              context: t('profitSharingPlanBook.form.title'),
              message
            })
          }
        })
      }
      const handleReset = () => {
        initForm()
      }

      const getContacts = async (search = '') => {
        store.dispatch('organizations/GET_COMPANY_CONTACTS', {
          companyId: currentCompany.value.id,
          filter: {
            limit: 100,
            offset: 0,
            name: search
          }
        })
      }
      const toggleModal = (state: boolean) =>
        store.dispatch('ui/toggleModal', { display: state })

      watch(plan, (newValue) => {
        if (newValue) {
          newPlan.value = {
            colorCode: newValue.colorCode || null,
            name: newValue.name,
            description: newValue.description || null
          }
        }
      })

      watch(modalForm, (newValues) => {
        if (newValues.initCurrentForm) resetForm()
      })

      onMounted(() => {
        getContacts()
      })

      return {
        newPlan,
        plan,
        canDo,
        schema,
        colorCode,
        toggleModal,
        handleForm,
        deleteGroup,
        handleReset
      }
    }
  })
</script>
<style lang="scss" scoped>
  .text-area {
    @apply resize-none w-full p-2 rounded border border-secondary4 focus:border-primary4 text-secondary9;
    font-size: 0.9em;
  }
</style>
